import { motion, useReducedMotion } from "framer-motion";
import { Award, DollarSign, Clock, Shield, MessageSquare, CheckCircle2 } from "lucide-react";

const benefits = [
  {
    icon: Award,
    title: "Proven Experience",
    description: "Over 18 years in residential construction with 150+ completed projects across Greater Houston",
  },
  {
    icon: DollarSign,
    title: "Clear, Fixed Quotes",
    description: "Detailed, line-by-line quote before work begins, so you know exactly what you're paying for",
  },
  {
    icon: Clock,
    title: "On-Time Delivery",
    description: "Realistic project timelines and weekly updates. Most bathroom and kitchen remodels completed in 4–8 weeks",
  },
  {
    icon: Shield,
    title: "Licensed, Insured & Code-Compliant",
    description: "All work meets or exceeds local building codes and inspection requirements",
  },
  {
    icon: MessageSquare,
    title: "Customer-First Communication",
    description: "Dedicated project manager, clear milestones, and prompt responses within one business day",
  },
  {
    icon: CheckCircle2,
    title: "1-Year Warranty & Maintenance",
    description: "12-month labor warranty on remodeling projects, with ongoing maintenance plan options",
  },
];

export default function WhyChooseHHSBuild() {
  const prefersReducedMotion = useReducedMotion();

  const containerVariants = {
    hidden: { opacity: prefersReducedMotion ? 1 : 0 },
    visible: {
      opacity: 1,
      transition: prefersReducedMotion ? { duration: 0 } : {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: prefersReducedMotion ? 1 : 0, scale: prefersReducedMotion ? 1 : 0.9 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: prefersReducedMotion ? { duration: 0 } : {
        duration: 0.5,
        ease: [0.22, 1, 0.36, 1],
      },
    },
  };

  return (
    <section className="py-16 md:py-24 relative overflow-hidden" data-testid="section-why-choose">
      <div className="absolute inset-0 bg-gradient-to-b from-[#00d9ff]/10 via-transparent to-transparent pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 md:px-8 relative z-10">
        <motion.div
          initial={{ opacity: prefersReducedMotion ? 1 : 0, y: prefersReducedMotion ? 0 : -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={prefersReducedMotion ? { duration: 0 } : { duration: 0.6 }}
          className="text-center mb-12 md:mb-16"
        >
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold gradient-text mb-4 tracking-tight" data-testid="text-why-choose-title">
            Why Houston Homeowners Choose HHS Build
          </h2>
          <p className="text-lg md:text-xl text-[#e0e0e0]/70 max-w-3xl mx-auto" data-testid="text-why-choose-subtitle">
            Renovating your home is a big decision. We make the process clear, predictable, and as stress-free as possible.
          </p>
        </motion.div>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-50px" }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10"
        >
          {benefits.map((benefit, index) => {
            const Icon = benefit.icon;
            return (
              <motion.div
                key={index}
                variants={itemVariants}
                className="glass-card p-6 md:p-8 hover-lift hover-glow-cyan transition-all duration-500"
                data-testid={`card-why-choose-${index}`}
              >
                <div className="mb-4">
                  <div className="bg-[#00d9ff]/20 backdrop-blur-md p-4 rounded-xl border border-[#00d9ff]/30 inline-block">
                    <Icon className="h-8 w-8 text-[#00d9ff]" data-testid={`icon-why-choose-${index}`} />
                  </div>
                </div>
                <h3 className="text-xl md:text-2xl font-heading font-semibold text-[#00d9ff] mb-3" data-testid={`text-why-choose-benefit-title-${index}`}>
                  {benefit.title}
                </h3>
                <p className="text-[#e0e0e0]/70 leading-relaxed" data-testid={`text-why-choose-benefit-description-${index}`}>
                  {benefit.description}
                </p>
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
}
